import 'react18-json-view/src/style.css'
import { format } from 'date-fns'
import { Check, Clock, RotateCcw, X } from 'lucide-react'
import { useParams } from 'react-router'
import JsonView from 'react18-json-view'
import { Badge } from '@/components/sv/badge'
import { CopyField } from '@/components/sv/copy-field'
import { H4, Muted, Small } from '@/components/sv/typographies'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  WebhookEventDetailsQuery,
  WebhookEventDetailsQueryVariables,
} from '@/routes/webhook/tabs/event-deliveries/event-details/index.types.gen'

const WEBHOOK_EVENT_DETAILS = gql`
  query WebhookEventDetails(
    $idCompany: String!
    $idWebhook: String!
    $id: String!
  ) {
    company(id: $idCompany) {
      id
      webhook(id: $idWebhook) {
        id
        eventDelivery(id: $id) {
          id
          idEvent
          endpoint
          type
          status
          isRecovered
          payload
          response
          responseTimeMs
          responseStatus
          sentAt
          createdAt
        }
      }
    }
  }
`

const isJson = (str: string) => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

export const EventDetails = () => {
  const { idCompany } = useIdCompany()
  const { idWebhook = '', idEventDelivery = '' } = useParams()

  const { data } = useSuspenseQuery<
    WebhookEventDetailsQuery,
    WebhookEventDetailsQueryVariables
  >(WEBHOOK_EVENT_DETAILS, {
    variables: { idCompany, idWebhook, id: idEventDelivery },
  })
  const event = data?.company?.webhook?.eventDelivery

  if (!event) {
    return <div className="p-6">No event found.</div>
  }

  return (
    <div className="space-y-0">
      <Card className="p-6 border-0 shadow-none gap-2">
        <CardHeader className="p-0">
          <CardTitle>
            <H4>Details</H4>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-[auto_1fr] gap-y-2 gap-x-6 items-center">
            <Muted>Type</Muted>
            <Small className="font-mono">{event.type}</Small>
            <Muted>Status</Muted>
            {event.status === 'PENDING' ? (
              <Badge variant="outline" className="font-mono">
                <Clock aria-hidden="true" /> Pending
              </Badge>
            ) : null}
            {event.status === 'SUCCESS' ? (
              <Badge variant="emerald">
                <Check aria-hidden="true" /> Success
              </Badge>
            ) : null}
            {event.status === 'FAILED' && !event.isRecovered ? (
              <Badge variant="destructive">
                <X aria-hidden="true" /> Failed
              </Badge>
            ) : null}
            {event.status === 'FAILED' && event.isRecovered ? (
              <Badge variant="emerald">
                <RotateCcw aria-hidden="true" /> Failed - Recovered
              </Badge>
            ) : null}
            <Muted>Attempt date</Muted>
            <Small>
              {format(
                event?.sentAt ? event?.sentAt : event?.createdAt,
                'MMM dd, yyyy, HH:mm:ss'
              )}
            </Small>
            <Muted>Endpoint</Muted>
            <CopyField value={event?.endpoint} size="sm" />
            <Muted>Event ID</Muted>
            <CopyField value={event?.idEvent} size="sm" />
          </div>
        </CardContent>
      </Card>
      <Separator />
      <Card className="p-6 border-0 shadow-none gap-2">
        <CardHeader className="p-0">
          <CardTitle>
            <H4>Request</H4>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isJson(event?.payload ?? '') ? (
            <JsonView
              src={JSON.parse(event.payload ?? '')}
              className="border rounded-lg p-2 text-xs"
            />
          ) : (
            <div className="border rounded-lg p-2 text-xs">
              {event?.payload}
            </div>
          )}
        </CardContent>
      </Card>
      <Separator />
      {event.status !== 'PENDING' ? (
        <Card className="p-6 border-0 shadow-none gap-2">
          <CardHeader className="p-0">
            <CardTitle>
              <H4>Response</H4>
            </CardTitle>
            <CardDescription>
              HTTP status code <strong>{event.responseStatus ?? 'N/A'}</strong>{' '}
              ({event.responseTimeMs} ms)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isJson(event?.response ?? '') ? (
              <JsonView
                src={JSON.parse(event.response ?? '')}
                className="border rounded-lg p-2 text-xs"
              />
            ) : (
              <div className="border rounded-lg p-2 text-xs">
                {event.response ?? 'No response'}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
